import React, { useState } from 'react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { 
  CheckCircle2, 
  FileText,
  Play,
  ChevronLeft, 
  ChevronRight
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Custom components for the lecture
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6 rounded-r">
      <div className="flex items-start">
        <CheckCircle2 className="h-6 w-6 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-blue-900">{children}</div>
      </div>
    </div>
  );
};

const ScientificDiagram = ({ 
  title, 
  caption, 
  children 
}: { 
  title: string; 
  caption: string; 
  children: React.ReactNode;
}) => {
  return (
    <figure className="my-8 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 mt-1">{caption}</p>
      </div>
      <div className="overflow-hidden">
        {children}
      </div>
    </figure>
  );
};



// Slideshow component
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
}

const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };
  
  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  return (
    <div className="mb-8">
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-medium">Lecture Slideshow</h3>
        <div className="flex gap-2">
          <button
            onClick={goToPrevSlide}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-sm text-gray-500">
            {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={goToNextSlide}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-medium">{slides[currentSlide].title}</h2>
        </div>
        <div className="p-6">
          {slides[currentSlide].content}
        </div>
      </div>
    </div>
  );
};

const TestosteroneMolecularFoundations: React.FC = () => {




  const lectureSlides = [
    {
      id: 1,
      title: "Introduction to Testosterone",
      content: (
        <>
          <p className="mb-4">Testosterone is the principal male sex hormone and an anabolic steroid that plays critical roles in human physiology. Beyond its well-known effects on male reproductive development and function, testosterone exerts widespread influence on nearly every organ system. Optimal testosterone levels are increasingly recognized as important for <strong>chronic disease prevention, longevity, and health optimization</strong>.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Clinical Significance</h3>
              <p>Low testosterone (hypogonadism) in men has been correlated with higher risks of cardiovascular disease, metabolic syndrome, type 2 diabetes, osteoporosis, sarcopenia, cognitive decline, and all-cause mortality. Lower testosterone levels are associated with higher risk of death in men.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Epidemiological Evidence</h3>
              <p>Large cohort analyses have shown that men with normal-high testosterone tend to have lower all-cause mortality. The Testosterone Trials (TTrials) demonstrated that testosterone therapy improved bone density, increased hemoglobin, and modestly improved sexual function and mood in hypogonadal men.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Testosterone is far more than a sex hormone. It plays crucial roles in metabolic health, body composition, cognitive function, cardiovascular health, bone integrity, and immune modulation. Understanding its molecular mechanisms provides insights into why low testosterone increases disease risk.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 2,
      title: "Testosterone Structure and Chemistry",
      content: (
        <>
          <ScientificDiagram 
            title="Testosterone Chemical Structure" 
            caption="Molecular structure of testosterone showing its steroid nucleus and functional groups."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Testosterone Chemical Structure</text>
                
                {/* Testosterone Structure */}
                <g transform="translate(400, 220)">
                  {/* Steroid nucleus (simplified) */}
                  <path d="M-140,0 L-60,0 L-20,60 L20,0 L100,0 L140,60 L100,120 L20,120 L-20,60 L-60,120 L-140,120 Z" 
                        fill="#D6EAF8" stroke="#3498DB" strokeWidth="3" />
                  
                  {/* OH group */}
                  <circle cx="100" cy="0" r="16" fill="#AED6F1" stroke="#3498DB" strokeWidth="2" />
                  <text x="100" y="5" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">OH</text>
                  
                  {/* Methyl groups */}
                  <circle cx="-120" cy="30" r="16" fill="#AED6F1" stroke="#3498DB" strokeWidth="2" />
                  <text x="-120" y="35" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">CH₃</text>
                  
                  <circle cx="0" cy="150" r="16" fill="#AED6F1" stroke="#3498DB" strokeWidth="2" />
                  <text x="0" y="155" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">CH₃</text>
                  
                  {/* Chemical formula */}
                  <text x="0" y="200" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#3498DB">C₁₉H₂₈O₂</text>
                  <text x="0" y="230" textAnchor="middle" fontSize="16" fill="#3498DB">Molecular Weight: 288.42 g/mol</text>
                </g>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Chemical Properties</h3>
              <p>Testosterone is a 19-carbon steroid hormone with a characteristic four-ring structure. It contains a ketone group at C3 and a hydroxyl group at C17, making it an androgen. Due to its predominately lipophilic nature, testosterone can cross cell membranes but requires protein carriers for transport in the bloodstream.</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="font-medium text-amber-900 mb-2">Structural Modifications</h3>
              <p>The testosterone molecule can be enzymatically modified to produce more potent androgens (such as dihydrotestosterone via 5α-reductase) or estrogens (via aromatase). These structural modifications dramatically alter the hormone's receptor binding affinity and biological effects.</p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 3,
      title: "Biosynthesis and Production",
      content: (
        <>
          <ScientificDiagram 
            title="Testosterone Biosynthesis Pathway" 
            caption="Major steps in testosterone synthesis from cholesterol, showing key enzymes and intermediate metabolites."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Testosterone Biosynthesis Pathway</text>
                
                {/* Biosynthesis Pathway */}
                <g transform="translate(400, 230)">
                  {/* Cholesterol */}
                  <rect x="-300" y="-150" width="120" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                  <text x="-240" y="-135" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">Cholesterol</text>
                  
                  {/* First enzyme */}
                  <text x="-160" y="-135" textAnchor="middle" fontSize="12" fill="#8E44AD">CYP11A1</text>
                  <path d="M-180,-135 L-140,-135" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                  
                  {/* Pregnenolone */}
                  <rect x="-120" y="-150" width="120" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                  <text x="-60" y="-135" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">Pregnenolone</text>
                  
                  {/* Second enzyme */}
                  <text x="20" y="-135" textAnchor="middle" fontSize="12" fill="#8E44AD">CYP17A1</text>
                  <path d="M0,-135 L40,-135" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                  
                  {/* DHEA */}
                  <rect x="60" y="-150" width="120" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                  <text x="120" y="-135" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">DHEA</text>
                  
                  {/* Third enzyme */}
                  <text x="180" y="-110" textAnchor="middle" fontSize="12" fill="#8E44AD">3β-HSD</text>
                  <path d="M120,-120 L120,-65" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                  
                  {/* Androstenedione */}
                  <rect x="60" y="-50" width="120" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                  <text x="120" y="-35" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">Androstenedione</text>
                  
                  {/* Fourth enzyme */}
                  <text x="20" y="-35" textAnchor="middle" fontSize="12" fill="#8E44AD">17β-HSD</text>
                  <path d="M60,-35 L0,-35" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                  
                  {/* Testosterone */}
                  <rect x="-120" y="-50" width="120" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                  <text x="-60" y="-35" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">Testosterone</text>
                  
                  {/* Fifth enzyme */}
                  <text x="-160" y="-10" textAnchor="middle" fontSize="12" fill="#8E44AD">5α-reductase</text>
                  <path d="M-60,-20 L-60,30" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                  
                  {/* DHT */}
                  <rect x="-120" y="50" width="120" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                  <text x="-60" y="65" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">DHT</text>
                </g>
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#8E44AD" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Primary Production Sites</h3>
              <p>In men, Leydig cells in the testes produce approximately 95% of circulating testosterone, stimulated by luteinizing hormone (LH) from the pituitary. The adrenal glands contribute about 5%. In women, testosterone is produced in smaller amounts, primarily by the ovaries and adrenal glands, with additional conversion from precursors in peripheral tissues.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Regulatory Control</h3>
              <p>Testosterone production is regulated by the hypothalamic-pituitary-gonadal (HPG) axis. Gonadotropin-releasing hormone (GnRH) from the hypothalamus stimulates the pituitary to secrete LH, which then signals Leydig cells to produce testosterone. Negative feedback loops help maintain homeostatic control of testosterone levels.</p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 4,
      title: "Classical Genomic Pathway",
      content: (
        <>
          <ScientificDiagram 
            title="Classical Androgen Receptor Signaling" 
            caption="Testosterone's genomic mechanism of action via androgen receptor activation and gene transcription."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Classical Genomic Pathway</text>
                
                {/* Cell outline */}
                <rect x="100" y="100" width="600" height="300" rx="10" fill="#f9f9f9" stroke="#ddd" strokeWidth="2" />
                
                {/* Cell membrane */}
                <rect x="100" y="120" width="600" height="10" rx="2" fill="#AED6F1" stroke="#3498DB" strokeWidth="1" />
                <text x="150" y="110" textAnchor="middle" fontSize="12" fill="#3498DB">Extracellular</text>
                <text x="150" y="145" textAnchor="middle" fontSize="12" fill="#3498DB">Intracellular</text>
                
                {/* Testosterone */}
                <path d="M200,85 L220,95 L240,85 L260,95 L260,75 L240,65 L220,75 L200,65 Z" fill="#AED6F1" stroke="#3498DB" strokeWidth="2" />
                <text x="230" y="155" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#3498DB">T</text>
                
                {/* 5α-reductase */}
                <rect x="300" y="75" width="80" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="2" />
                <text x="340" y="95" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">5α-reductase</text>
                
                {/* DHT */}
                <path d="M430,85 L450,95 L470,85 L490,95 L490,75 L470,65 L450,75 L430,65 Z" fill="#AED6F1" stroke="#3498DB" strokeWidth="2" />
                <text x="460" y="155" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#3498DB">DHT</text>
                
                {/* Androgen Receptor */}
                <rect x="300" y="180" width="150" height="40" rx="8" fill="#D6EAF8" stroke="#3498DB" strokeWidth="2" />
                <text x="375" y="205" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#3498DB">Androgen Receptor</text>
                
                {/* T-AR complex */}
                <rect x="300" y="260" width="150" height="40" rx="8" fill="#D6EAF8" stroke="#3498DB" strokeWidth="2" />
                <text x="375" y="285" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#3498DB">T/DHT-AR Complex</text>
                
                {/* Nucleus */}
                <ellipse cx="375" cy="350" rx="150" ry="40" fill="#EBF5FB" stroke="#3498DB" strokeWidth="2" />
                <text x="375" y="335" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#3498DB">Nucleus</text>
                <text x="375" y="360" textAnchor="middle" fontSize="12" fill="#3498DB">DNA (Androgen Response Elements)</text>
                <text x="375" y="380" textAnchor="middle" fontSize="12" fill="#3498DB">Target Gene Regulation</text>
                
                {/* Action arrows */}
                <path d="M260,90 L300,90" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                <path d="M380,90 L430,90" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                <path d="M230,120 L300,180" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                <path d="M460,120 L400,180" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                <path d="M375,220 L375,260" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                <path d="M375,300 L375,320" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3498DB" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <h3 className="font-medium text-blue-900 mb-2">Genomic Signaling Mechanisms</h3>
            <p>In the classical genomic pathway, testosterone (or its more potent metabolite dihydrotestosterone, DHT) diffuses through the cell membrane and binds to the androgen receptor (AR) in the cytoplasm. This binding induces a conformational change in the AR, causing dissociation of heat shock proteins and receptor dimerization. The hormone-receptor complex then translocates to the nucleus where it binds to specific DNA sequences called androgen response elements (AREs), recruiting co-activators or co-repressors to modulate gene transcription.</p>
          </div>
          
          <KeyTakeaway>
            The genomic actions of testosterone typically take hours to manifest as they require new protein synthesis. Genes regulated by testosterone include those involved in protein anabolism, cell growth, differentiation, and metabolism. This pathway is responsible for many of the long-term developmental and physiological effects of testosterone.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 5,
      title: "Non-Genomic Signaling Pathways",
      content: (
        <>
          <ScientificDiagram 
            title="Rapid Non-Genomic Testosterone Signaling" 
            caption="Membrane-initiated signaling cascades activated by testosterone that produce rapid cellular effects."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Non-Genomic/Rapid Signaling</text>
                
                {/* Cell outline */}
                <rect x="100" y="100" width="600" height="300" rx="10" fill="#f9f9f9" stroke="#ddd" strokeWidth="2" />
                
                {/* Cell membrane */}
                <rect x="100" y="120" width="600" height="10" rx="2" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1" />
                
                {/* Testosterone */}
                <path d="M200,85 L220,95 L240,85 L260,95 L260,75 L240,65 L220,75 L200,65 Z" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="2" />
                <text x="230" y="110" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">T</text>
                
                {/* Membrane receptors */}
                <rect x="180" y="130" width="70" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                <text x="215" y="150" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">mAR</text>
                
                <rect x="280" y="130" width="70" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                <text x="315" y="150" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">GPRC6A</text>
                
                <rect x="380" y="130" width="70" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                <text x="415" y="150" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">SHBG-R</text>
                
                {/* Signaling cascades */}
                <path d="M215,160 L215,200 L250,220 L250,250" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                <path d="M315,160 L315,200 L350,220 L350,250" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                <path d="M415,160 L415,200 L450,220 L450,250" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                
                {/* Signaling proteins */}
                <rect x="200" y="250" width="100" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                <text x="250" y="270" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">MAPK/ERK</text>
                
                <rect x="310" y="250" width="100" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                <text x="360" y="270" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">PI3K/Akt</text>
                
                <rect x="420" y="250" width="70" height="30" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                <text x="455" y="270" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Ca²⁺</text>
                
                {/* Downstream effects */}
                <rect x="200" y="320" width="290" height="40" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="2" />
                <text x="345" y="345" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">Rapid Non-Genomic Effects</text>
                
                {/* Action arrows */}
                <path d="M230,95 L215,130" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                <path d="M250,280 L250,320" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                <path d="M360,280 L360,320" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                <path d="M455,280 L455,320" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                
                {/* Physiological effects */}
                <text x="180" y="390" textAnchor="middle" fontSize="12" fill="#8E44AD">• Protein Synthesis</text>
                <text x="345" y="390" textAnchor="middle" fontSize="12" fill="#8E44AD">• Cell Survival</text>
                <text x="500" y="390" textAnchor="middle" fontSize="12" fill="#8E44AD">• Glucose Uptake</text>
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#8E44AD" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="bg-purple-50 p-4 rounded-md mb-6">
            <h3 className="font-medium text-purple-900 mb-2">Rapid Non-Genomic Mechanisms</h3>
            <p>In addition to its genomic effects, testosterone can trigger rapid cellular responses within seconds to minutes, too fast to involve gene transcription. These non-genomic actions occur through various mechanisms including: (1) binding to membrane-associated androgen receptors (mARs), (2) interaction with G-protein coupled receptors such as GPRC6A, (3) crosstalk with growth factor receptors, and (4) modulation of ion channels. These interactions activate signaling cascades like MAPK/ERK, PI3K/Akt, and calcium signaling pathways.</p>
          </div>
          
          <KeyTakeaway>
            The non-genomic actions of testosterone complement its genomic effects and are particularly important in tissues requiring rapid responses, such as neurons, vascular endothelium, and muscle cells. They contribute to testosterone's effects on vasodilation, muscle contraction, insulin sensitivity, and neurotransmission. Understanding these pathways provides insights into potential therapeutic targets for tissue-specific androgen modulation.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 6,
      title: "Mitochondrial and Metabolic Effects",
      content: (
        <>
          <p className="mb-4">Testosterone has significant effects on cellular energy metabolism and mitochondrial function, which underlie many of its physiological benefits. These effects are particularly prominent in metabolically active tissues such as skeletal muscle, adipose tissue, and the brain.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-green-50 p-4 rounded-md">
              <h3 className="font-medium text-green-900 mb-2">Mitochondrial Biogenesis</h3>
              <p>Testosterone upregulates key regulators of mitochondrial biogenesis, including PGC-1α (peroxisome proliferator-activated receptor gamma coactivator 1-alpha) and TFAM (mitochondrial transcription factor A). This leads to increased mitochondrial density and respiratory capacity, particularly in skeletal muscle. Studies show that testosterone administration increases mitochondrial number, cristae density, and expression of electron transport chain components.</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="font-medium text-amber-900 mb-2">Metabolic Flexibility</h3>
              <p>Testosterone enhances metabolic flexibility—the ability to efficiently switch between carbohydrate and fat oxidation based on substrate availability and energy demands. It promotes insulin-stimulated glucose uptake in muscle via enhanced GLUT4 translocation while simultaneously supporting efficient fatty acid oxidation. This dual action helps maintain optimal fuel utilization and energy homeostasis.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">mTOR Pathway Activation</h3>
              <p>Testosterone activates the mammalian target of rapamycin (mTOR) pathway, a central regulator of cell growth and metabolism. Through PI3K/Akt signaling, testosterone increases mTOR phosphorylation and activates p70 S6 kinase, stimulating protein synthesis and inhibiting protein degradation. This mechanism is central to testosterone's anabolic effects in skeletal muscle.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Redox Balance</h3>
              <p>Optimal testosterone levels help maintain cellular redox balance by upregulating antioxidant defenses and reducing excessive ROS production. Physiological testosterone enhances expression of antioxidant enzymes such as superoxide dismutase (SOD) and glutathione peroxidase (GPx), while low testosterone states are associated with increased oxidative stress markers and mitochondrial dysfunction.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Testosterone's effects on mitochondrial function and energy metabolism represent a fundamental mechanism behind its health benefits. By enhancing mitochondrial biogenesis, supporting metabolic flexibility, and maintaining redox balance, testosterone promotes cellular health and resilience. These effects likely contribute to the associations between healthy testosterone levels and reduced risk of metabolic syndrome, type 2 diabetes, and age-related conditions.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 7,
      title: "Inflammation and Immune Modulation",
      content: (
        <>
          <p className="mb-4">Testosterone exerts significant modulatory effects on inflammation and immune function, creating a complex relationship that impacts various disease processes and aging trajectories.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Anti-Inflammatory Effects</h3>
              <p>Physiological testosterone levels generally exert anti-inflammatory effects in men. Hypogonadal men show elevated levels of pro-inflammatory cytokines (IL-6, TNF-α, IL-1β) and acute phase proteins (CRP), which decrease with testosterone restoration. Mechanistically, testosterone suppresses NF-κB signaling, a master regulator of inflammatory gene expression, and reduces immune cell activation and cytokine production.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Immune Cell Modulation</h3>
              <p>Testosterone influences multiple immune cell populations. It typically reduces pro-inflammatory T helper 1 (Th1) responses while potentially enhancing T helper 2 (Th2) activity. It modulates macrophage polarization toward an anti-inflammatory M2 phenotype rather than the pro-inflammatory M1 state. Testosterone also affects neutrophil activity and B-cell antibody production, generally exerting an immunomodulatory rather than purely suppressive effect.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="font-medium text-amber-900 mb-2">Adipose Tissue Inflammation</h3>
              <p>One of testosterone's most important anti-inflammatory actions occurs in adipose tissue. Low testosterone promotes visceral adiposity, which becomes infiltrated with pro-inflammatory immune cells (macrophages, T-cells) that drive systemic inflammation. Testosterone therapy reduces visceral fat mass and its inflammatory output, particularly in hypogonadal men with metabolic syndrome or obesity.</p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-md">
              <h3 className="font-medium text-emerald-900 mb-2">Autoimmunity and Infection</h3>
              <p>Testosterone's immunomodulatory effects help explain sex differences in autoimmune and infectious disease susceptibility. Physiological testosterone appears protective against autoimmunity (which is more common in women), potentially by balancing inflammatory/anti-inflammatory T-cell subsets. However, these same effects may reduce immune responses to certain infections, highlighting the complex trade-offs in immune regulation.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The anti-inflammatory and immunomodulatory effects of testosterone are increasingly recognized as important contributors to its health benefits in men. These effects may help explain why low testosterone is associated with chronic inflammatory conditions including atherosclerosis, metabolic syndrome, and neurodegenerative diseases. However, optimal immune function requires balanced testosterone levels—both deficiency and excess can disrupt immune homeostasis.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 8,
      title: "Systems Biology Perspective",
      content: (
        <>
          <ScientificDiagram 
            title="Multi-System Effects of Testosterone" 
            caption="An overview of the key physiological systems affected by testosterone and the primary effects in each system."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Multi-System Effects of Testosterone</text>
                
                {/* Central icon - Testosterone */}
                <circle cx="400" cy="180" r="60" fill="#f9f9f9" stroke="#333" strokeWidth="2" />
                <text x="400" y="170" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Testosterone</text>
                <text x="400" y="195" textAnchor="middle" fontSize="12" fill="#666">Systemic</text>
                <text x="400" y="210" textAnchor="middle" fontSize="12" fill="#666">Regulation</text>
                
                {/* Musculoskeletal System */}
                <g transform="translate(200, 130)">
                  <rect x="-100" y="-40" width="200" height="80" rx="10" fill="#f1f8ff" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="-20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#3498DB">Musculoskeletal</text>
                  <text x="0" y="0" textAnchor="middle" fontSize="11" fill="#3498DB">• ↑ Muscle protein synthesis</text>
                  <text x="0" y="20" textAnchor="middle" fontSize="11" fill="#3498DB">• ↑ Bone mineral density</text>
                </g>
                
                {/* Metabolic System */}
                <g transform="translate(200, 240)">
                  <rect x="-100" y="-40" width="200" height="80" rx="10" fill="#f8f1ff" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="-20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">Metabolic</text>
                  <text x="0" y="0" textAnchor="middle" fontSize="11" fill="#8E44AD">• ↑ Insulin sensitivity</text>
                  <text x="0" y="20" textAnchor="middle" fontSize="11" fill="#8E44AD">• ↓ Visceral adiposity</text>
                </g>
                
                {/* Cardiovascular System */}
                <g transform="translate(600, 130)">
                  <rect x="-100" y="-40" width="200" height="80" rx="10" fill="#FEF9E7" stroke="#F1C40F" strokeWidth="1.5" />
                  <text x="0" y="-20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#F1C40F">Cardiovascular</text>
                  <text x="0" y="0" textAnchor="middle" fontSize="11" fill="#F1C40F">• ↑ Vasodilation</text>
                  <text x="0" y="20" textAnchor="middle" fontSize="11" fill="#F1C40F">• ↓ Inflammation</text>
                </g>
                
                {/* Neurological System */}
                <g transform="translate(600, 240)">
                  <rect x="-100" y="-40" width="200" height="80" rx="10" fill="#EBF5FB" stroke="#2874A6" strokeWidth="1.5" />
                  <text x="0" y="-20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">Neurological</text>
                  <text x="0" y="0" textAnchor="middle" fontSize="11" fill="#2874A6">• ↑ Neuroprotection</text>
                  <text x="0" y="20" textAnchor="middle" fontSize="11" fill="#2874A6">• ↑ Cognitive function</text>
                </g>
                
                {/* Immune System */}
                <g transform="translate(400, 350)">
                  <rect x="-100" y="-40" width="200" height="80" rx="10" fill="#FDEDEC" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="0" y="-20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#E74C3C">Immune</text>
                  <text x="0" y="0" textAnchor="middle" fontSize="11" fill="#E74C3C">• ↓ Pro-inflammatory cytokines</text>
                  <text x="0" y="20" textAnchor="middle" fontSize="11" fill="#E74C3C">• ↑ Immune homeostasis</text>
                </g>
                
                {/* Connection lines */}
                <line x1="340" y1="180" x2="290" y2="150" stroke="#3498DB" strokeWidth="2" />
                <line x1="340" y1="180" x2="290" y2="220" stroke="#8E44AD" strokeWidth="2" />
                <line x1="460" y1="180" x2="510" y2="150" stroke="#F1C40F" strokeWidth="2" />
                <line x1="460" y1="180" x2="510" y2="220" stroke="#2874A6" strokeWidth="2" />
                <line x1="400" y1="240" x2="400" y2="310" stroke="#E74C3C" strokeWidth="2" />
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-3 rounded-md">
              <h3 className="font-medium text-blue-900 mb-1">Musculoskeletal</h3>
              <p className="text-sm">Testosterone promotes protein synthesis, satellite cell activation, and fiber hypertrophy in muscle, while increasing osteoblast activity and inhibiting osteoclast-mediated bone resorption.</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-md">
              <h3 className="font-medium text-purple-900 mb-1">Metabolic</h3>
              <p className="text-sm">Testosterone enhances insulin sensitivity, glucose metabolism, and metabolic flexibility while reducing visceral adiposity—effects that improve overall metabolic health.</p>
            </div>
            <div className="bg-amber-50 p-3 rounded-md">
              <h3 className="font-medium text-amber-900 mb-1">Cardiovascular</h3>
              <p className="text-sm">Healthy testosterone levels promote cardiovascular health through enhanced nitric oxide-dependent vasodilation, reduced inflammation, improved lipid profiles, and endothelial function.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-emerald-50 p-3 rounded-md">
              <h3 className="font-medium text-emerald-900 mb-1">Neurological</h3>
              <p className="text-sm">In the brain, testosterone enhances neuroprotection, synaptic plasticity, spatial cognition, and may protect against amyloid-β accumulation and neurodegeneration.</p>
            </div>
            <div className="bg-rose-50 p-3 rounded-md">
              <h3 className="font-medium text-rose-900 mb-1">Immune</h3>
              <p className="text-sm">Testosterone modulates immune function, typically reducing pro-inflammatory cytokines, affecting T-cell differentiation, and helping maintain immune homeostasis.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The systems biology perspective reveals testosterone as a master regulator that coordinates physiological responses across multiple organ systems. This cross-system integration explains why testosterone deficiency manifests with such diverse symptoms and why optimization can yield wide-ranging benefits. Rather than viewing testosterone narrowly as a reproductive hormone, understanding its role in integrated physiology provides insights into its contribution to healthspan and longevity.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 9,
      title: "Testosterone Assessment Methods",
      content: (
        <>
          <ScientificDiagram 
            title="Testosterone Testing and Assessment" 
            caption="Comprehensive approaches to testosterone measurement and clinical interpretation."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Testosterone Assessment Approaches</text>
                
                {/* Laboratory Testing */}
                <g transform="translate(200, 180)">
                  <rect x="-160" y="-70" width="320" height="200" rx="10" fill="#f1f8ff" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">Laboratory Testing</text>
                  
                  {/* Total T */}
                  <rect x="-140" y="-20" width="120" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-80" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Total Testosterone</text>
                  
                  {/* Free T */}
                  <rect x="20" y="-20" width="120" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="80" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Free Testosterone</text>
                  
                  {/* Binding proteins */}
                  <rect x="-140" y="40" width="120" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-80" y="55" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Binding Proteins</text>
                  <text x="-80" y="75" textAnchor="middle" fontSize="10" fill="#3498DB">SHBG, Albumin</text>
                  
                  {/* Related hormones */}
                  <rect x="20" y="40" width="120" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="80" y="55" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Related Hormones</text>
                  <text x="80" y="75" textAnchor="middle" fontSize="10" fill="#3498DB">Estradiol, LH, FSH</text>
                  
                  {/* Methods */}
                  <rect x="-140" y="100" width="280" height="20" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-70" y="114" textAnchor="middle" fontSize="10" fill="#3498DB">LC-MS/MS</text>
                  <text x="0" y="114" textAnchor="middle" fontSize="10" fill="#3498DB">Immunoassay</text>
                  <text x="70" y="114" textAnchor="middle" fontSize="10" fill="#3498DB">Equilibrium Dialysis</text>
                </g>
                
                {/* Clinical Interpretation */}
                <g transform="translate(600, 180)">
                  <rect x="-160" y="-70" width="320" height="200" rx="10" fill="#f8f1ff" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">Clinical Interpretation</text>
                  
                  {/* Age considerations */}
                  <rect x="-140" y="-20" width="120" height="40" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-80" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Age Factors</text>
                  <text x="-80" y="15" textAnchor="middle" fontSize="10" fill="#8E44AD">Age-related decline</text>
                  
                  {/* Diurnal variation */}
                  <rect x="20" y="-20" width="120" height="40" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="80" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Chronobiology</text>
                  <text x="80" y="15" textAnchor="middle" fontSize="10" fill="#8E44AD">Morning sampling</text>
                  
                  {/* Medical factors */}
                  <rect x="-140" y="40" width="280" height="65" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="60" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Medical Considerations</text>
                  <text x="0" y="80" textAnchor="middle" fontSize="10" fill="#8E44AD">• Medications (opioids, glucocorticoids)</text>
                  <text x="0" y="95" textAnchor="middle" fontSize="10" fill="#8E44AD">• Obesity (↑ aromatization to estradiol)</text>
                </g>
                
                {/* Androgen Sensitivity */}
                <g transform="translate(400, 350)">
                  <rect x="-300" y="-30" width="600" height="80" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  <text x="0" y="-5" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#111">Androgen Sensitivity Assessment</text>
                  
                  {/* Genetic */}
                  <g transform="translate(-200, 30)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#E74C3C">Genetic Factors</text>
                    <text x="0" y="20" textAnchor="middle" fontSize="10" fill="#E74C3C">AR CAG repeat length</text>
                  </g>
                  
                  {/* Clinical */}
                  <g transform="translate(0, 30)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#F1C40F">Clinical Phenotype</text>
                    <text x="0" y="20" textAnchor="middle" fontSize="10" fill="#F1C40F">Physical characteristics</text>
                  </g>
                  
                  {/* Biomarkers */}
                  <g transform="translate(200, 30)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#27AE60">Response Markers</text>
                    <text x="0" y="20" textAnchor="middle" fontSize="10" fill="#27AE60">SHBG, metabolites</text>
                  </g>
                </g>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Laboratory Assessment</h3>
              <p>Comprehensive testosterone assessment includes measurement of total testosterone (preferably by LC-MS/MS for accuracy), calculation or direct measurement of free testosterone, and assessment of relevant binding proteins (SHBG, albumin). Timing is important—testosterone levels follow a circadian rhythm with peak concentrations in the morning. Multiple measurements may be necessary due to pulsatile secretion and day-to-day variability.</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Clinical Interpretation</h3>
              <p>Interpreting testosterone levels requires consideration of multiple factors: (1) Age-related reference ranges versus optimal physiological ranges, (2) Signs and symptoms of deficiency or excess, (3) Free versus total testosterone, (4) Concurrent medical conditions, and (5) Medications that affect testosterone. Low testosterone manifests differently among individuals due to variations in androgen sensitivity and other hormonal factors.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Accurate testosterone assessment requires more than simple measurement of total testosterone. A comprehensive approach includes evaluation of free testosterone, binding proteins, and related hormones, with consideration of timing, method accuracy, and individual variation in androgen sensitivity. Clinical interpretation should integrate laboratory values with symptoms, age considerations, and the patient's overall health status to guide appropriate management decisions.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 10,
      title: "Testosterone Therapy Approaches",
      content: (
        <>
          <ScientificDiagram 
            title="Testosterone Therapy Options" 
            caption="Various therapeutic approaches for testosterone optimization with their respective pharmacokinetics and clinical considerations."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Testosterone Therapy Approaches</text>
                
                {/* Injectable Forms */}
                <g transform="translate(200, 180)">
                  <rect x="-160" y="-70" width="320" height="200" rx="10" fill="#f1f8ff" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">Injectable Formulations</text>
                  
                  {/* Types */}
                  <rect x="-140" y="-20" width="280" height="25" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-110" y="-8" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Types:</text>
                  <text x="40" y="-8" textAnchor="middle" fontSize="10" fill="#3498DB">Cypionate, Enanthate, Undecanoate, Propionate</text>
                  
                  {/* PK */}
                  <g transform="translate(-70, 50)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Pharmacokinetics</text>
                    <text x="0" y="20" textAnchor="middle" fontSize="10" fill="#3498DB">• Cypionate: 8-10 day half-life</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="10" fill="#3498DB">• Undecanoate: ~34 day half-life</text>
                    <text x="0" y="50" textAnchor="middle" fontSize="10" fill="#3498DB">• Peak and trough variability</text>
                  </g>
                  
                  {/* Pros/Cons */}
                  <g transform="translate(90, 50)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Considerations</text>
                    <text x="0" y="20" textAnchor="middle" fontSize="10" fill="#3498DB">+ Cost-effective</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="10" fill="#3498DB">+ Reliable absorption</text>
                    <text x="0" y="50" textAnchor="middle" fontSize="10" fill="#3498DB">- Injections required</text>
                  </g>
                </g>
                
                {/* Topical & Transdermal */}
                <g transform="translate(600, 180)">
                  <rect x="-160" y="-70" width="320" height="200" rx="10" fill="#f8f1ff" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">Topical & Transdermal</text>
                  
                  {/* Types */}
                  <rect x="-140" y="-20" width="280" height="25" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-110" y="-8" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">Types:</text>
                  <text x="40" y="-8" textAnchor="middle" fontSize="10" fill="#8E44AD">Gels, Creams, Patches, Nasal Sprays</text>
                  
                  {/* PK */}
                  <g transform="translate(-70, 50)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Pharmacokinetics</text>
                    <text x="0" y="20" textAnchor="middle" fontSize="10" fill="#8E44AD">• Daily application needed</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="10" fill="#8E44AD">• More physiologic levels</text>
                    <text x="0" y="50" textAnchor="middle" fontSize="10" fill="#8E44AD">• 10-15% bioavailability</text>
                  </g>
                  
                  {/* Pros/Cons */}
                  <g transform="translate(90, 50)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Considerations</text>
                    <text x="0" y="20" textAnchor="middle" fontSize="10" fill="#8E44AD">+ Easy application</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="10" fill="#8E44AD">+ Steady levels</text>
                    <text x="0" y="50" textAnchor="middle" fontSize="10" fill="#8E44AD">- Transfer risk to others</text>
                  </g>
                </g>
                
                {/* Other Options */}
                <g transform="translate(400, 350)">
                  <rect x="-300" y="-30" width="600" height="80" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  <text x="0" y="-5" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#111">Alternative Approaches</text>
                  
                  {/* Pellets */}
                  <g transform="translate(-200, 30)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#F1C40F">Subcutaneous Pellets</text>
                    <text x="0" y="20" textAnchor="middle" fontSize="10" fill="#F1C40F">3-6 month duration</text>
                  </g>
                  
                  {/* Oral */}
                  <g transform="translate(0, 30)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#27AE60">Oral Formulations</text>
                    <text x="0" y="20" textAnchor="middle" fontSize="10" fill="#27AE60">Limited hepatic first-pass</text>
                  </g>
                  
                  {/* Lifestyle */}
                  <g transform="translate(200, 30)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#E74C3C">Endogenous Support</text>
                    <text x="0" y="20" textAnchor="middle" fontSize="10" fill="#E74C3C">Lifestyle, supplements</text>
                  </g>
                </g>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Injectable Testosterone</h3>
              <p>Injectable testosterone esters remain the most widely used form of testosterone replacement therapy. Testosterone cypionate and enanthate are administered every 1-2 weeks, while testosterone undecanoate offers a longer duration of action (10-14 weeks). The primary advantage is reliable absorption and cost-effectiveness, though the pharmacokinetic profile creates peaks and troughs in serum levels. More frequent, smaller doses (e.g., twice weekly) can create more stable levels.</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Topical Applications</h3>
              <p>Transdermal testosterone formulations include gels, creams, and patches applied daily. These provide more physiological hormone patterns without the peaks and troughs associated with injections. However, they have variable absorption, potential for transfer to others (especially children and women), and skin irritation. Newer formulations like testosterone nasal spray offer alternative delivery with reduced transfer risk.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-amber-50 p-3 rounded-md">
              <h3 className="font-medium text-amber-900 mb-1">Subcutaneous Pellets</h3>
              <p className="text-sm">Testosterone pellets implanted subcutaneously provide sustained release over 3-6 months. This approach offers convenience but requires a minor surgical procedure and has less flexibility for dose adjustment.</p>
            </div>
            <div className="bg-emerald-50 p-3 rounded-md">
              <h3 className="font-medium text-emerald-900 mb-1">Oral Formulations</h3>
              <p className="text-sm">Oral testosterone undecanoate is absorbed via the lymphatic system, bypassing first-pass liver metabolism. While convenient, bioavailability is limited and must be taken with fatty meals for absorption.</p>
            </div>
            <div className="bg-rose-50 p-3 rounded-md">
              <h3 className="font-medium text-rose-900 mb-1">Endogenous Support</h3>
              <p className="text-sm">For some men, optimizing endogenous production through lifestyle (sleep, exercise, stress management) and targeted supplements may be sufficient to restore healthier testosterone levels.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Testosterone therapy selection should be individualized based on patient preferences, lifestyle factors, comorbidities, and goals of treatment. Each delivery method has distinct pharmacokinetic profiles and practical considerations. Regardless of the approach, ongoing monitoring is essential to ensure that treatment achieves physiologic testosterone levels while minimizing potential adverse effects. The goal should be optimizing health outcomes rather than simply achieving numerical targets.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 11,
      title: "Monitoring and Safety Considerations",
      content: (
        <>
          <p className="mb-4">Proper monitoring is essential for safe and effective testosterone therapy. A comprehensive approach tracks physiological responses across multiple parameters while watching for potential adverse effects.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Laboratory Monitoring</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Testosterone levels (total and free)</li>
                <li>Complete blood count (hematocrit/hemoglobin)</li>
                <li>Comprehensive metabolic panel</li>
                <li>Lipid profile</li>
                <li>PSA and prostate assessment (in men)</li>
                <li>Estradiol (to monitor aromatization)</li>
                <li>Thyroid function tests</li>
              </ul>
              <p className="mt-2">Monitoring frequency typically includes baseline assessment, follow-up at 3-6 months, and then annually once stable. More frequent monitoring may be necessary when adjusting dosage or with certain risk factors.</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="font-medium text-amber-900 mb-2">Clinical Parameters</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Body composition (weight, BMI, waist circumference)</li>
                <li>Blood pressure and cardiovascular markers</li>
                <li>Symptom assessment (energy, mood, cognition, libido)</li>
                <li>Functional measures (strength, physical performance)</li>
                <li>Sleep quality</li>
                <li>Assessment of adverse effects</li>
              </ul>
              <p className="mt-2">Clinical response varies between individuals due to differences in androgen sensitivity, metabolic factors, and concurrent conditions. Treatment should be guided by symptom improvement alongside laboratory values rather than focusing solely on numerical targets.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Safety Considerations</h3>
              <p>Potential concerns with testosterone therapy include:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Erythrocytosis (elevated hematocrit) – the most common adverse effect</li>
                <li>Potential cardiovascular effects (controversial, with evidence suggesting safety in appropriate candidates)</li>
                <li>Effects on prostate health in men (current evidence does not support causation of prostate cancer)</li>
                <li>Sleep apnea exacerbation in predisposed individuals</li>
                <li>Fertility suppression (spermatogenesis inhibition)</li>
                <li>Acne, hair growth changes, and other androgenic effects</li>
              </ul>
              <p className="mt-2">Many adverse effects are dose-dependent and avoidable with proper monitoring and dosage adjustment. The risk-benefit profile varies based on individual factors and concurrent conditions.</p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-md">
              <h3 className="font-medium text-emerald-900 mb-2">Optimizing Treatment Response</h3>
              <p>Several factors influence treatment effectiveness:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Dosage – finding the minimal effective dose to achieve health benefits</li>
                <li>Timing – considering circadian rhythms and physiological patterns</li>
                <li>Delivery method – selecting appropriate formulations based on individual needs</li>
                <li>Adjunctive therapies – addressing related factors (e.g., thyroid function, cortisol levels)</li>
                <li>Lifestyle optimization – exercise, nutrition, sleep, and stress management</li>
                <li>Metabolic health – insulin sensitivity, inflammation, oxidative balance</li>
              </ul>
              <p className="mt-2">A comprehensive approach that integrates hormone optimization with lifestyle modification yields better outcomes than focusing on testosterone replacement alone.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Testosterone therapy requires careful monitoring and individualized assessment of benefits versus risks. When properly administered with appropriate follow-up, it can safely address symptoms and health risks associated with testosterone deficiency. The emphasis should be on restoring physiological hormone balance rather than supraphysiological levels, with the goal of optimizing long-term health outcomes across multiple systems.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 12,
      title: "Future Directions and Conclusion",
      content: (
        <>
          <p className="mb-4">As our understanding of testosterone's systemic effects continues to evolve, several emerging areas hold promise for advancing hormone optimization strategies and improving health outcomes.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Emerging Research Areas</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Tissue-Selective Androgen Receptor Modulators (SARMs)</strong> – Compounds designed to provide androgen effects in specific tissues while minimizing unwanted effects in others</li>
                <li><strong>Genetic Polymorphisms</strong> – Personalization of therapy based on individual variations in androgen receptor sensitivity, metabolism, and response</li>
                <li><strong>Novel Delivery Systems</strong> – Development of improved formulations with more physiological hormone patterns and convenient administration</li>
                <li><strong>Mitochondrial and Metabolic Targeting</strong> – Approaches that specifically enhance testosterone's beneficial effects on cellular energy metabolism</li>
                <li><strong>Hormone Crosstalk Integration</strong> – Understanding the complex interactions between testosterone and other hormonal systems (thyroid, cortisol, insulin, etc.)</li>
              </ul>
            </div>
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Clinical Translation</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Precision Medicine Approaches</strong> – Moving beyond standardized reference ranges to individualized optimization based on molecular and genetic factors</li>
                <li><strong>Expanded Therapeutic Applications</strong> – Based on testosterone's diverse effects, potential applications in neurodegenerative disorders, metabolic diseases, and sarcopenia</li>
                <li><strong>Integrative Protocols</strong> – Combining hormone optimization with targeted lifestyle, nutritional, and pharmaceutical interventions for synergistic effects</li>
                <li><strong>Improved Diagnostic Testing</strong> – Development of better biomarkers and assessment methods for androgen status and sensitivity</li>
                <li><strong>Longitudinal Outcome Studies</strong> – Long-term research on health outcomes with various approaches to testosterone optimization</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-amber-50 p-4 rounded-md mb-6">
            <h3 className="font-medium text-amber-900 mb-2">Clinical Perspective</h3>
            <p>The molecular understanding of testosterone's actions provides a scientific foundation for clinical practice. Rather than viewing testosterone narrowly as a sex hormone, clinicians should recognize its role as a systemic regulator that influences virtually all physiological systems. This broader perspective helps explain the diverse manifestations of testosterone deficiency and the multi-system benefits of optimization.</p>
            <p className="mt-2">When considering testosterone therapy, the goal should be physiological optimization rather than supraphysiological supplementation. Treatment decisions should be guided by a comprehensive assessment of symptoms, laboratory values, risk factors, and individual goals, with ongoing monitoring to ensure safety and efficacy. The benefits of testosterone optimization are most pronounced when integrated with broader health-promoting strategies, including proper nutrition, physical activity, stress management, and sleep optimization.</p>
          </div>
          
          <KeyTakeaway>
            Testosterone represents a master regulatory hormone with profound effects across multiple physiological systems. By understanding its cellular and molecular mechanisms, we gain insights into why testosterone deficiency increases disease risk and why optimization offers widespread health benefits. Future advances in personalized approaches to testosterone assessment and therapy hold promise for enhancing healthspan and addressing the increasing burden of age-related conditions. As research continues to refine our understanding, the integration of testosterone science into clinical practice provides an opportunity to advance preventive medicine and optimize health outcomes.
          </KeyTakeaway>
        </>
      )
    }
  ];


  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-2">Testosterone: Molecular Foundations</h1>
              <p className="text-lg text-gray-600">A Systems Biology Approach to Testosterone</p>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-3 py-1 rounded-full flex items-center text-sm mt-2 md:mt-0">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              <span>Mentorship Content</span>
            </div>
          </div>
          
          <Separator className="mb-6" />
          
          <Slideshow slides={lectureSlides} />
        </div>
      </main>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}

export default TestosteroneMolecularFoundations;